"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <>
      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden hover:!bg-transparent focus:!bg-transparent" aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild className="relative group hover:!bg-transparent focus:!bg-transparent px-4 py-2 text-sm font-medium transition-all duration-[var(--duration-medium)]">
                <Link to={item.href || "#"}>
                  {item.label}
                  <span className="absolute bottom-1 left-4 right-4 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)]" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-background border-b shadow-medium py-4 px-6 space-y-2">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block py-2 text-sm font-medium hover:text-foreground transition-colors duration-[var(--duration-fast)]" onClick={() => setMobileMenuOpen(false)}>
              {item.label}
            </Link>)}
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`fixed top-0 z-50 w-full bg-background border-b transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] h-22 shadow-medium`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/#" className="flex-shrink-0">
          <span className="font-display text-2xl md:text-3xl font-bold text-foreground">
            Marlon Lupo
          </span>
        </Link>

        <MenuProvider menu_id="3" className="flex-1 flex justify-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <Button asChild className="hover:!bg-primary-hover focus:!bg-primary-hover hover:scale-105 transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
  <Link to="/#contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium
    [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0
    outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]
    aria-invalid:ring-destructive/20 aria-invalid:border-destructive
    text-primary-foreground shadow-xs h-9 px-4 py-2 has-[>svg]:px-3
    transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]
    bg-blue-800 hover:bg-blue-700 focus:bg-blue-700
    disabled:pointer-events-none disabled:opacity-50
    hover:scale-105">
    Get Started
  </Link>
</Button>
      </div>
    </header>;
}